import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import { useEditor } from 'client/widget-components/customHooks/useEditor';
import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import BookingCancellationWrapper from './components/ConfirmationModal/BookingCancellationWrapper';
import BookingConfirmWrapper from './components/ConfirmationModal/BookingConfirmWrapper';
import ContainerQueryStyles from './components/ContainerQueryStyles';
import HiddenStylesHelper from './components/HiddenStylesHelper';
import RescheduleModalWrapper from './components/RescheduleModalWrapper';
import { CurrentStepComponent } from './components/steps';
import { BookerContextProvider } from './Context';
import { useBookerColors } from './hooks/useBookerColors';
import { useResolveUrlProps } from './hooks/useResolveUrlProps';
import { useRuntimeCalBookingState } from './hooks/useRuntimeCalBookingState';
import { NativeBookingWrapper } from './nativeBookerStyles';
import { CalBookingWrapper } from './styles';
import { BookerContextType, CalBookingProps, DesignStyle } from './types';
import { isBookingDryRun as isBookingDryRunUtil } from './utils';

const RuntimeCalBooking: RuntimeWidget<CalBookingProps> = (
    props: CalBookingProps
) => {
    const {
        clientId,
        featureMetadata,
        widgetId,
        organizationId,
        eventIdentifier,
        events,
        designStyle: designStyleProp,
        groupedAppointmentInfo,
        serviceProviderStage,
        _styles,
        bookingSource,
    } = props;
    const [state, actions] = useRuntimeCalBookingState(props);
    const editor = useEditor();
    const isBookingDryRun = isBookingDryRunUtil(editor, props.featureMetadata);

    useResolveUrlProps({
        ...props,
        isBookingDryRun,
        setRescheduleUid: actions.setRescheduleUid,
        setCancelBookingId: actions.setCancelBookingId,
        setEventSlug: actions.setEventSlug,
        setTeamId: actions.setTeamId,
        setUserName: actions.setUserName,
        setSelectedHostId: actions.setSelectedHostId,
    });

    // backward compatibility with single event booking
    const isBookingActive =
        (props.events?.length ?? 0) >= 1 || !!state.eventSlug;
    const designStyle = designStyleProp || DesignStyle.SITE_THEME;
    const currentEvent = useMemo(
        () => events?.find?.((event) => event.slug === state.eventSlug) || null,
        [events, state.eventSlug]
    );

    const nativeEnabled = props.isNativeBookerEnabled ?? false;

    const BookingWrapper = nativeEnabled
        ? NativeBookingWrapper
        : CalBookingWrapper;

    const bookerTheme = useBookerColors(_styles, designStyle);
    // The reschedule modal should always render with the basic theme,
    // regardless of the widget's design style (site-theme vs basic).
    const basicTheme = useBookerColors(_styles, DesignStyle.BASIC);

    const bookerContext: BookerContextType = {
        ...featureMetadata,
        eventSlug: state.eventSlug,
        teamId: state.teamId,
        userName: state.userName,
        rescheduleUid: state.rescheduleUid,
        cancelBookingId: state.cancelBookingId,
        widgetId,
        designStyle,
        language: state.language,
        apiUrl: state.apiUrl,
        clientId,
        organizationId,
        currentEvent,
        eventIdentifier,
        isBookingActive,
        isBookingDryRun,
        events: events || [],
        groupedAppointmentInfo,
        serviceProviderStage,
        showFreePrice: props.showFreePrice ?? true,
        isNativeBookerEnabled: nativeEnabled,
        styles: _styles,
        bookingSource,
    };

    if (!isBookingActive && !editor.isInPreviewOrEditor()) {
        return null;
    }

    return (
        <BookerContextProvider
            {...bookerContext}
            state={state}
            actions={actions}
        >
            <ThemeProvider theme={bookerTheme}>
                <BookingWrapper
                    data-auto='ssr-cal-booking-widget'
                    className={`booking-wrapper ${designStyle}`}
                >
                    {!nativeEnabled && (
                        <ContainerQueryStyles widgetId={widgetId} />
                    )}
                    <HiddenStylesHelper />
                    <CurrentStepComponent {...props} />
                </BookingWrapper>
            </ThemeProvider>
            <ThemeProvider theme={basicTheme}>
                <BookingCancellationWrapper />
                <BookingConfirmWrapper />
                <RescheduleModalWrapper />
            </ThemeProvider>
        </BookerContextProvider>
    );
};

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
